import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, FileSpreadsheet, Paperclip, Plus, Settings2 } from 'lucide-react';
import type { LeaveBalance, LeavePlanner, LeaveRequest, LeaveRequestList, LeaveStatement, LeaveSummary } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { pick } from '../../i18n';
import { Badge, EmptyState, Modal, PageHeader, TableSkeleton, cn } from '../../components/ui';
import { PersonLine } from '../people/shared';
import { RequestDetail, RequestForm } from './requests';
import { parseLeaveSearch, type LeaveSearch, type LeaveTab } from './search';
import { BalanceCard, StatusBadge, TypeDot, fmtDate, fmtPeriod, useHours } from './shared';

const TABS: LeaveTab[] = ['mine', 'approvals', 'planner'];

/** Leave for everyone: my balance and requests, what awaits my decision, and who of my team is away when. */
export function LeavePage() {
  const { t } = useTranslation();
  const { can, canAction } = useAuth();
  const navigate = useNavigate();
  const search = parseLeaveSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const { id: openId } = useParams({ strict: false }) as { id?: string };
  const [filing, setFiling] = useState(false);
  const summary = useQuery({ queryKey: ['leave', 'summary'], queryFn: () => api.get<LeaveSummary>('/api/leave/summary'), staleTime: 15_000 });
  const tab: LeaveTab = search.tab ?? 'mine';
  const setSearch = (patch: Partial<LeaveSearch>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    void navigate({ to: '/leave', search: next as LeaveSearch });
  };
  const open = (id: string | null) => void navigate(id ? { to: '/leave/requests/$id', params: { id }, search } : { to: '/leave', search });
  const s = summary.data;

  return (
    <div>
      <PageHeader
        title={t('leave.pageTitle')}
        subtitle={s ? t('leave.awayToday', { count: s.away_today }) : undefined}
        actions={
          <>
            {can('leave_admin') && <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/leave/admin', search: {} })}><Settings2 size={16} /> {t('leave.adminPage')}</button>}
            {s?.is_employee && <button type="button" className="btn-primary" onClick={() => setFiling(true)}><Plus size={16} /> {t('leave.newRequest')}</button>}
          </>
        }
      />
      <div className="mb-4 flex overflow-hidden rounded-lg border border-neutral-300 text-sm dark:border-neutral-700 sm:inline-flex">
        {TABS.map((v) => (
          <button key={v} type="button" onClick={() => setSearch({ tab: v === 'mine' ? undefined : v })} className={cn('flex flex-1 items-center justify-center gap-2 px-3 py-1.5', tab === v ? 'bg-brand-600 text-white' : 'bg-white dark:bg-neutral-900')}>
            {t(`leave.tabs.${v}`)}
            {v === 'approvals' && (s?.inbox_total ?? 0) > 0 && <span className={cn('rounded-full px-1.5 text-[10px] font-semibold', tab === v ? 'bg-white text-brand-700' : 'bg-amber-500 text-white')}>{s!.inbox_total}</span>}
          </button>
        ))}
      </div>
      {tab === 'mine' && <MyLeave isEmployee={s?.is_employee} onOpen={open} onFile={() => setFiling(true)} />}
      {tab === 'approvals' && <Approvals history={Boolean(search.history)} onHistory={(on) => setSearch({ history: on ? '1' : undefined })} onOpen={open} canSeeAll={canAction('leave.manage')} />}
      {tab === 'planner' && <Planner month={search.month} everyone={Boolean(search.everyone)} canSeeAll={canAction('leave.manage')} onChange={(patch) => setSearch(patch)} />}
      <RequestForm open={filing} onClose={() => setFiling(false)} hoursPerDay={s?.hours_per_day ?? 8} />
      <RequestDetail id={openId ?? null} onClose={() => open(null)} />
    </div>
  );
}

function RequestRow({ r, showPerson, onOpen }: { r: LeaveRequest; showPerson?: boolean; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const h = useHours();
  return (
    <li>
      <button type="button" onClick={onOpen} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-start text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/60">
        {showPerson && <span className="w-full min-w-0 sm:w-56"><PersonLine person={r.employee} /></span>}
        <TypeDot type={r.leave_type} className="w-36 font-medium" />
        <span className="min-w-0 flex-1">{fmtPeriod(r.start_date, r.end_date, i18n.language)}</span>
        <span className="tabular-nums text-neutral-600 dark:text-neutral-300">{h.days(r.days)} · {h.hours(r.hours)}</span>
        {r.needs_attachment && r.attachment_count === 0 && ['pending', 'pending_hr'].includes(r.status) && <Badge tone="warn"><Paperclip size={10} /> {t('leave.documentShort')}</Badge>}
        <StatusBadge status={r.status} />
      </button>
    </li>
  );
}

function MyLeave({ isEmployee, onOpen, onFile }: { isEmployee?: boolean; onOpen: (id: string) => void; onFile: () => void }) {
  const { t } = useTranslation();
  const h = useHours();
  const [showStatement, setShowStatement] = useState(false);
  const balance = useQuery({ queryKey: ['leave', 'me', 'balance'], queryFn: () => api.get<LeaveBalance>('/api/leave/me/balance'), enabled: isEmployee === true, retry: false });
  const requests = useQuery({ queryKey: ['leave', 'requests', 'mine'], queryFn: () => api.get<LeaveRequestList>('/api/leave/requests?scope=mine&page_size=100'), enabled: isEmployee === true });
  if (isEmployee === undefined) return <TableSkeleton />;
  if (!isEmployee) return <EmptyState>{t('leave.notEmployee')}</EmptyState>;
  const b = balance.data;
  const used = (b?.types ?? []).filter((x) => x.used_hours > 0 || x.pending_hours > 0);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="grid grid-cols-1 content-start gap-4">
        {balance.isError && <EmptyState>{t('leave.noContract')}</EmptyState>}
        {b && <BalanceCard balance={b} />}
        <div className="card">
          <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
            <h2 className="font-semibold">{t('leave.myRequests')} <span className="text-xs font-normal text-neutral-500">({requests.data?.meta.total ?? 0})</span></h2>
            <div className="flex gap-2">
              {b && <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setShowStatement(true)}>{t('leave.statement')}</button>}
              <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={onFile}><Plus size={12} /> {t('leave.newRequest')}</button>
            </div>
          </div>
          {requests.data?.items.length === 0 && <p className="p-4 text-sm text-neutral-500">{t('leave.noRequests')}</p>}
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">{(requests.data?.items ?? []).map((r) => <RequestRow key={r.id} r={r} onOpen={() => onOpen(r.id)} />)}</ul>
        </div>
      </div>
      <div className="grid grid-cols-1 content-start gap-4">
        <div className="card p-4">
          <h2 className="mb-2 font-semibold">{t('leave.otherTypes')}</h2>
          {b && used.length === 0 && <p className="text-sm text-neutral-500">{t('leave.otherTypesNone')}</p>}
          <ul className="grid gap-3">
            {used.map((x) => (
              <li key={x.leave_type.id} className="text-sm">
                <div className="flex items-center justify-between gap-2"><TypeDot type={x.leave_type} /><span className="text-xs tabular-nums text-neutral-500">{x.max_hours === null ? h.both(x.used_hours, b!.contract.hours_per_day) : `${h.n(x.used_hours)} / ${h.hours(x.max_hours)}`}</span></div>
                {x.max_hours !== null && <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800" dir="ltr"><div className="h-full rounded-full" style={{ width: `${Math.min(100, ((x.used_hours + x.pending_hours) / x.max_hours) * 100)}%`, background: x.leave_type.colour }} /></div>}
                {x.pending_hours > 0 && <div className="mt-0.5 text-xs text-amber-600">{t('leave.pendingOfType', { value: h.hours(x.pending_hours) })}</div>}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <StatementDialog open={showStatement} onClose={() => setShowStatement(false)} url="/api/leave/me/statement" />
    </div>
  );
}

/** What the balance is made of: the derived accrual month by month and every stored movement. */
export function StatementDialog({ open, onClose, url }: { open: boolean; onClose: () => void; url: string }) {
  const { t, i18n } = useTranslation();
  const h = useHours();
  const st = useQuery({ queryKey: ['leave', 'statement', url], queryFn: () => api.get<LeaveStatement>(url), enabled: open });
  // The running figure follows the annual balance only (accrual lines carry the annual type) and stops at the statement's date.
  const rows = useMemo(() => {
    const entries = st.data?.entries ?? [];
    const annualId = entries.find((e) => e.movement === 'accrual')?.leave_type.id;
    let running = 0;
    return entries.map((e) => {
      const counts = e.leave_type.id === annualId && e.effective_date <= (st.data?.balance.as_of ?? '');
      if (counts) running = Math.round((running + e.hours) * 100) / 100;
      return { e, running: counts ? running : null };
    });
  }, [st.data]);
  return (
    <Modal open={open} onClose={onClose} wide title={t('leave.statement')} footer={<><button type="button" className="btn-secondary" onClick={() => void api.download(`${url.replace(/\?.*$/, '')}/export${url.includes('?') ? url.slice(url.indexOf('?')) + '&' : '?'}locale=${i18n.language === 'ar' ? 'ar' : 'en'}`)}><FileSpreadsheet size={16} /> {t('app.exportExcel')}</button><button type="button" className="btn-secondary" onClick={onClose}>{t('app.close')}</button></>}>
      {st.data && (
        <div className="grid gap-3">
          <BalanceCard balance={st.data.balance} compact />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-start text-xs text-neutral-500"><tr><th className="py-1 text-start font-medium">{t('leave.date')}</th><th className="py-1 text-start font-medium">{t('leave.movement')}</th><th className="py-1 text-start font-medium">{t('leave.type')}</th><th className="py-1 text-end font-medium">{t('leave.hoursHeader')}</th><th className="py-1 text-end font-medium">{t('leave.runningAnnual')}</th></tr></thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {rows.map(({ e, running }) => {
                  return (
                    <tr key={e.id}>
                      <td className="py-1.5 whitespace-nowrap">{fmtDate(e.effective_date, i18n.language)}</td>
                      <td className="py-1.5">{t(`leave.movements.${e.movement}`)}{e.reason && <span className="block text-xs text-neutral-500" dir="auto">{e.reason}</span>}</td>
                      <td className="py-1.5"><TypeDot type={e.leave_type} /></td>
                      <td className={cn('py-1.5 text-end tabular-nums', e.hours < 0 ? 'text-red-600' : 'text-brand-700 dark:text-brand-100')} dir="ltr">{e.hours > 0 ? '+' : ''}{h.n(e.hours)}</td>
                      <td className="py-1.5 text-end tabular-nums text-neutral-500" dir="ltr">{running === null ? '' : h.n(running)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Approvals({ history, onHistory, onOpen, canSeeAll }: { history: boolean; onHistory: (on: boolean) => void; onOpen: (id: string) => void; canSeeAll: boolean }) {
  const { t } = useTranslation();
  const inbox = useQuery({ queryKey: ['leave', 'requests', 'inbox'], queryFn: () => api.get<LeaveRequestList>('/api/leave/requests?scope=inbox&page_size=100&sort=created_at') });
  const team = useQuery({ queryKey: ['leave', 'requests', canSeeAll ? 'all' : 'team'], queryFn: () => api.get<LeaveRequestList>(`/api/leave/requests?scope=${canSeeAll ? 'all' : 'team'}&page_size=100`), enabled: history });
  return (
    <div className="grid gap-4">
      <div className="card">
        <div className="border-b border-neutral-200 px-4 py-2 dark:border-neutral-800"><h2 className="font-semibold">{t('leave.toDecide')} <span className="text-xs font-normal text-neutral-500">({inbox.data?.meta.total ?? 0})</span></h2><p className="text-xs text-neutral-500">{t('leave.toDecideHint')}</p></div>
        {inbox.data?.items.length === 0 && <p className="p-4 text-sm text-neutral-500">{t('leave.nothingToDecide')}</p>}
        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">{(inbox.data?.items ?? []).map((r) => <RequestRow key={r.id} r={r} showPerson onOpen={() => onOpen(r.id)} />)}</ul>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={history} onChange={(e) => onHistory(e.target.checked)} />{canSeeAll ? t('leave.showAll') : t('leave.showTeam')}</label>
      {history && (
        <div className="card">
          {team.data?.items.length === 0 && <p className="p-4 text-sm text-neutral-500">{t('leave.noRequests')}</p>}
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">{(team.data?.items ?? []).map((r) => <RequestRow key={r.id} r={r} showPerson onOpen={() => onOpen(r.id)} />)}</ul>
        </div>
      )}
    </div>
  );
}

const pad = (n: number) => String(n).padStart(2, '0');
const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
};

/**
 * A month as a grid: one row per person, a coloured cell per day of leave; days off and holidays shaded. Cells
 * inform and do not open the request: most viewers may know THAT a colleague is away, not why.
 */
function Planner({ month, everyone, canSeeAll, onChange }: { month?: string; everyone: boolean; canSeeAll: boolean; onChange: (patch: Partial<LeaveSearch>) => void }) {
  const { t, i18n } = useTranslation();
  const current = month ?? new Date().toISOString().slice(0, 7);
  const days = useMemo(() => {
    const [y, m] = current.split('-').map(Number) as [number, number];
    return Array.from({ length: new Date(Date.UTC(y, m, 0)).getUTCDate() }, (_, i) => `${current}-${pad(i + 1)}`);
  }, [current]);
  const plan = useQuery({ queryKey: ['leave', 'planner', current, everyone], queryFn: () => api.get<LeavePlanner>(`/api/leave/planner${qs({ from: days[0], to: days[days.length - 1], scope: everyone ? 'all' : 'team' })}`), placeholderData: (prev) => prev });
  const holidays = new Map((plan.data?.holidays ?? []).map((d) => [d.date, pick(i18n.language, d.name_en, d.name_ar)]));
  const today = new Date().toISOString().slice(0, 10);
  const title = new Date(`${current}-01T00:00:00Z`).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const weekday = (d: string) => new Date(`${d}T00:00:00Z`).getUTCDay();
  const types = [...new Map((plan.data?.people ?? []).flatMap((p) => p.leaves.map((l) => [l.leave_type.code, l.leave_type] as const))).values()];

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <div className="flex items-center gap-1">
          <button type="button" className="btn-secondary px-2 py-1" onClick={() => onChange({ month: shiftMonth(current, -1) })} aria-label={t('app.previous')}><ChevronLeft size={16} className="rtl:rotate-180" /></button>
          <span className="w-40 text-center font-semibold">{title}</span>
          <button type="button" className="btn-secondary px-2 py-1" onClick={() => onChange({ month: shiftMonth(current, 1) })} aria-label={t('app.next')}><ChevronRight size={16} className="rtl:rotate-180" /></button>
          <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => onChange({ month: undefined })}>{t('leave.thisMonth')}</button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {types.map((x) => <TypeDot key={x.code} type={x} />)}
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-dashed border-neutral-500" />{t('leave.awaitingDecision')}</span>
          {canSeeAll && <label className="flex items-center gap-1.5"><input type="checkbox" checked={everyone} onChange={(e) => onChange({ everyone: e.target.checked ? '1' : undefined })} />{t('leave.everyone')}</label>}
        </div>
      </div>
      {plan.data?.people.length === 0 && <p className="p-6 text-center text-sm text-neutral-500">{t('leave.plannerEmpty')}</p>}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs" dir="ltr">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-48 bg-white px-3 py-1 text-start font-medium text-neutral-500 dark:bg-neutral-900">{t('leave.person')}</th>
              {days.map((d) => <th key={d} title={holidays.get(d)} className={cn('w-7 min-w-7 py-1 text-center font-normal text-neutral-500', d === today && 'font-semibold text-brand-700 dark:text-brand-100', holidays.has(d) && 'text-red-500')}>{Number(d.slice(8))}</th>)}
            </tr>
          </thead>
          <tbody>
            {(plan.data?.people ?? []).map((p) => (
              <tr key={p.employee.id} className="border-t border-neutral-100 dark:border-neutral-800">
                <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-sm dark:bg-neutral-900" dir="auto">
                  <PersonLine person={p.employee} size={24} />
                  {p.entities.length > 0 && <span className="block truncate ps-8 text-[11px] text-neutral-500">{p.entities.map((n) => pick(i18n.language, n.name_en, n.name_ar)).join(' · ')}</span>}
                </td>
                {days.map((d) => {
                  const leave = p.leaves.find((l) => l.start_date <= d && l.end_date >= d);
                  const off = !p.work_days.includes(weekday(d)) || holidays.has(d);
                  const waiting = leave && leave.status !== 'approved';
                  return (
                    <td key={d} className={cn('h-9 p-0.5', off && 'bg-neutral-100 dark:bg-neutral-800/60', d === today && 'outline outline-1 -outline-offset-1 outline-brand-500')}>
                      {leave && (
                        <div role="img" title={`${pick(i18n.language, leave.leave_type.name_en, leave.leave_type.name_ar)} · ${fmtPeriod(leave.start_date, leave.end_date, i18n.language)} · ${t(`leave.statuses.${leave.status}`)}`} className={cn('h-full w-full rounded', waiting && 'border border-dashed opacity-60', off && 'opacity-25')} style={{ background: waiting ? `${leave.leave_type.colour}55` : leave.leave_type.colour, borderColor: leave.leave_type.colour }} aria-label={`${pick(i18n.language, leave.leave_type.name_en, leave.leave_type.name_ar)} ${fmtPeriod(leave.start_date, leave.end_date, i18n.language)}`} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
